/**
 * TECHNICIAN BACKGROUND — "experienced, and OptiBay is still learning their
 * strengths", said plainly. Two sections that never blend into one bar:
 *
 *   Background          what they bring (stated; display only)
 *   OptiBay evidence    what OptiBay has observed, per category, with its state
 *
 * Mounted on the Passport (self and manager views). Not on operational cards.
 */
import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useSetTechnicianTradeBackground,
  useTechnicianEvidenceStates,
  useTechnicianTradeBackground,
} from '@/hooks/technicians/useTechnicianBackground';
import {
  describeTradeBackground,
  isExperienceLevel,
  optibayHistoryLine,
  type CategoryEvidenceView,
} from '@/lib/technicians/tradeBackground';
import { TechnicianBackgroundFields, type TechnicianBackgroundValue } from './TechnicianBackgroundFields';
import { useMechanicalTechnicianIds } from '@/hooks/useMechanicalTechnicianIds';

const STATE_TONE: Record<CategoryEvidenceView['state'], string> = {
  established: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  building: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  no_history: 'bg-muted text-muted-foreground',
};

/** "engine_diagnostics" / "Engine Diagnostics" → "Engine Diagnostics". */
export function categoryDisplayName(key: string): string {
  if (/[A-Z ]/.test(key)) return key;
  return key.split(/[_-]+/).map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');
}

export interface TechnicianBackgroundCardProps {
  shopId: string | null | undefined;
  shopTechnicianId: string | null | undefined;
  /** Stored fact: when this person's OptiBay record starts (employment / roster). */
  optibaySince?: string | null;
  /** Stored fact: completed jobs OptiBay has on record. */
  completedJobs?: number | null;
  /** The viewer may edit (self, or shop management). The server decides anyway. */
  canEdit?: boolean;
  perspective?: 'self' | 'other';
}

export function TechnicianBackgroundCard({
  shopId, shopTechnicianId, optibaySince, completedJobs, canEdit = false, perspective = 'other',
}: TechnicianBackgroundCardProps) {
  const bg = useTechnicianTradeBackground(shopId, shopTechnicianId);
  const evidence = useTechnicianEvidenceStates(shopTechnicianId);
  const save = useSetTechnicianTradeBackground();
  // Trade background is MECHANICAL background: a detail-only specialist is not
  // classified (their detailing evidence lives on the skill heatmap). The
  // work-role answer is the database's (shop_mechanical_technician_ids);
  // unknown (null) excludes nobody.
  const mechanical = useMechanicalTechnicianIds(shopId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<TechnicianBackgroundValue>({ experienceLevel: null, yearsProfessional: null });

  if (!shopId || !shopTechnicianId) return null;
  if (mechanical && !mechanical.has(shopTechnicianId)) return null;
  const view = describeTradeBackground(bg.data ?? null);
  const history = optibayHistoryLine({ since: optibaySince ?? null, completedJobs: completedJobs ?? null });
  const rows = evidence.data ?? [];

  const startEdit = () => {
    setDraft({
      experienceLevel: isExperienceLevel(bg.data?.experience_level) ? bg.data!.experience_level as TechnicianBackgroundValue['experienceLevel'] : null,
      yearsProfessional: bg.data?.years_professional ?? null,
    });
    setEditing(true);
  };

  return (
    <Card data-testid="technician-background-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">Technician background</CardTitle>
          {canEdit && !editing && (
            <Button size="sm" variant="ghost" className="h-9 gap-1" onClick={startEdit} data-testid="technician-background-edit">
              <Pencil className="h-3.5 w-3.5" /> {view.provided ? 'Edit' : 'Add'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {editing ? (
          <div className="space-y-3">
            <TechnicianBackgroundFields value={draft} onChange={setDraft} perspective={perspective} idPrefix="passport-bg" />
            <div className="flex flex-wrap gap-2">
              <Button
                className="h-10"
                disabled={save.isPending}
                data-testid="technician-background-save"
                onClick={() => save.mutateAsync({
                  shopId, technicianId: shopTechnicianId,
                  experienceLevel: draft.experienceLevel, yearsProfessional: draft.yearsProfessional,
                }).then(() => setEditing(false)).catch(() => { /* hook toasts */ })}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outline" className="h-10" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-0.5" data-testid="technician-background-summary">
            <div className={cn('font-semibold', !view.provided && 'text-muted-foreground font-normal')} data-testid="technician-background-headline">
              {view.headline}
            </div>
            {view.yearsLine && <div className="text-sm" data-testid="technician-background-years">{view.yearsLine}</div>}
            {view.sourceLine && <div className="text-xs text-muted-foreground">{view.sourceLine}</div>}
          </div>
        )}

        <div className="space-y-2 border-t pt-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">OptiBay evidence</div>
          {history && <div className="text-sm" data-testid="technician-optibay-history">{history}</div>}
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="technician-evidence-empty">
              Not enough OptiBay history yet. Category strengths appear here as OptiBay observes completed work.
            </p>
          ) : (
            <ul className="divide-y" data-testid="technician-evidence-list">
              {rows.map((r) => (
                <li key={r.categoryKey} className="py-2 flex flex-wrap items-start justify-between gap-x-3 gap-y-1" data-testid={`technician-evidence-${r.categoryKey}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{categoryDisplayName(r.categoryKey)}</div>
                    {r.basis && <div className="text-xs text-muted-foreground">{r.basis}</div>}
                    {r.openingEstimate && <div className="text-xs text-muted-foreground">{r.openingEstimate}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.scoreLabel && <span className="text-sm font-semibold">{r.scoreLabel}</span>}
                    <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', STATE_TONE[r.state])} data-state={r.state}>{r.label}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">
            Background is what the technician brings; evidence is what OptiBay has observed. Background never changes the measured figures.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
